import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, MessageSquareQuote } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

// AEO (Answer Engine Optimization) block: an answer-first card engineered to be
// quoted by AI answer engines (ChatGPT, Perplexity, Google AI Overviews, Gemini).
// The short, direct answer sits at the top; supporting detail is progressively disclosed.
export const AeoAnswerBlock = ({ question, answer, details, defaultOpen = false }) => {
  const { tx } = useI18n();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <article
      data-testid="aeo-answer-block"
      className="group rounded-2xl border border-foreground/10 bg-card p-6 transition-colors duration-200 hover:border-[hsl(var(--primary))]/40 sm:p-7"
    >
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--primary))]">
        <MessageSquareQuote className="h-3.5 w-3.5" />
        {tx("Answer")}
      </div>
      <h3 className="mt-3 font-display text-xl leading-snug text-foreground sm:text-2xl">{tx(question)}</h3>
      <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{tx(answer)}</p>
      {details && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="aeo-answer-toggle"
            className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-foreground/70 transition-colors hover:text-foreground"
          >
            {open ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {open ? tx("Less detail") : tx("More detail")}
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
              >
                <div className="mt-3 text-sm leading-relaxed text-muted-foreground">{details}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </article>
  );
};
